// frontend/src/components/payments/Payments.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, printReceipt, toDate, today } from '../common/CommonComponents';
import { SearchableDropdown } from '../common/CommonComponents';
import { getCachedPayments, cachePayments } from '../../services/cacheService';
console.log('SearchableDropdown imported:', typeof SearchableDropdown);

function Payments({ showAlert, user }) {
  const blank = { paymentId: "", orderId: "", amount: "", date: today(), method: "Cash", notes: "" };
  // A cancellation turns any money already received into a refund liability;
  // it must never remain an option for collecting another customer payment.
  const payableOrders = (orders) => (orders || []).filter(order =>
    order.orderStatus !== "Cancelled" && order.productionStatus !== "Delivered" && Number(order.balance) > 0
  );
  const [form, setForm] = useState(blank);
  const [mode, setMode] = useState("create");
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQ, setSearchQ] = useState("");
  const [methodF, setMethodF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeOrders, setActiveOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const paymentOperationId = useRef(null);
  const submitLock = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Reconciliation
  const [showRecon, setShowRecon] = useState(false);
  const [reconFrom, setReconFrom] = useState(today());
  const [reconTo, setReconTo] = useState(today());
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    
    // If offline, use cached data
    if (!navigator.onLine) {
      const cached = getCachedPayments();
      if (cached) {
        setPayments(cached.payments || []);
        setTotal(cached.total || 0);
        setLoading(false);
        showAlert("You are offline. Showing cached payments.", "info");
        return;
      }
    }
    
    // If online, fetch from server
    try {
      const r = await api.getPayments({ search: searchQ, page, limit: 20, method: methodF, dateFrom, dateTo });
      setPayments(r.payments);
      setTotal(r.total);
      // Save to cache
      cachePayments(r);
    } catch(e) {
      showAlert("Failed to load payments", "error");
    } finally {
      setLoading(false);
    }
  }, [searchQ, page, methodF, dateFrom, dateTo]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  useEffect(() => {
    api.getOrders({ limit: 500 })
      .then(r => setActiveOrders(payableOrders(r.orders)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedOrder(activeOrders.find(x => x.orderId === form.orderId) || null);
  }, [form.orderId, activeOrders]);

  const setFormField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const resetForm = () => {
    paymentOperationId.current = null;
    setForm(blank);
    setMode("create");
    setSelectedOrder(null);
    setShowForm(false);
  };

  const submit = async () => {
    if (submitLock.current) return;
    if (!form.orderId || !form.amount) {
      showAlert("Select an order and enter amount", "error");
      return;
    }
    if (mode === "create" && !activeOrders.some(order => order.orderId === form.orderId)) {
      showAlert("This order is closed for payment. Cancelled orders can only be refunded, and delivered orders are final.", "error");
      return;
    }
    submitLock.current = true;
    setSubmitting(true);
    try {
      if (mode === "create") {
        if (!paymentOperationId.current) paymentOperationId.current = window.crypto?.randomUUID?.() || `payment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        await api.createPayment({
          orderId: form.orderId,
          amount: Number(form.amount),
          date: form.date,
          method: form.method,
          notes: form.notes
        }, paymentOperationId.current);
        showAlert("Payment recorded!");
      } else {
        await api.updatePayment(form.paymentId, {
          amount: Number(form.amount),
          date: form.date,
          method: form.method,
          notes: form.notes
        });
        showAlert("Payment updated!");
      }
      resetForm();
      api.getOrders({ limit: 500 }).then(r => setActiveOrders(payableOrders(r.orders)));
      loadPayments();
    } catch (e) {
      showAlert(e.message, "error");
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const loadRecon = async () => {
    setReconLoading(true);
    try {
      setRecon(await api.getReconciliation(reconFrom, reconTo));
    } catch (e) {
      showAlert(e.message, "error");
    }
    setReconLoading(false);
  };

  const isManager = user.role === "manager";
  const canLogPayment = ["manager", "cashier"].includes(user.role) ||
    (user.role === "sales" && !!user.features?.salesCanLogPayments);

  // Method color mapping
  const getMethodColor = (method) => {
    const colors = {
      "Cash": "#10B981",
      "Transfer": "#3B82F6",
      "POS": "#F59E0B"
    };
    return colors[method] || "#94A3B8";
  };

  // Refunds belong to an order (which can have several instalments), so the
  // record shows the honest order-level outcome without assigning a refund to
  // the wrong individual payment.
  const paymentRefundState = (payment) => {
    if (payment.orderStatus !== "Cancelled") return null;
    const paid = Number(payment.orderTotalPaid || 0);
    const refunded = Number(payment.orderRefundedAmount || 0);
    if (paid > 0 && refunded >= paid) {
      return { label: "Refunded", detail: `₦${refunded.toLocaleString("en-NG")} returned to the customer`, complete: true };
    }
    if (refunded > 0) {
      return { label: "Partially refunded", detail: `₦${refunded.toLocaleString("en-NG")} of ₦${paid.toLocaleString("en-NG")} returned`, complete: false };
    }
    return { label: "Refund pending", detail: `₦${Math.max(paid - refunded, 0).toLocaleString("en-NG")} still due to the customer`, complete: false };
  };

  const sharePaymentReceipt = async (payment) => {
    const popup = window.open("", "_blank", "noreferrer");
    try {
      const links = await api.getOrderWhatsappLinks(payment.orderId, payment.paymentId);
      if (!links.hasPhone || !links.receiptLink) {
        if (popup) popup.close();
        showAlert("This order's customer needs a phone number before a WhatsApp receipt can be shared.", "error");
        return;
      }
      if (popup) popup.location.href = links.receiptLink;
      else window.open(links.receiptLink, "_blank", "noreferrer");
    } catch (e) {
      if (popup) popup.close();
      showAlert(e.message, "error");
    }
  };

  return (
    <div>
      {/* Header Section */}
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>💳 Payments</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
            Record customer payments, track collections, and reconcile accounts
          </p>
        </div>
        {canLogPayment && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Record Payment
          </button>
        )}
      </div>

      {/* Payment Form Modal/Card */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20, border: `2px solid ${mode === "update" ? "#F59E0B" : "var(--accent)"}` }}>
          <div className="section-header">
            <div>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                {mode === "create" ? "💰 Record New Payment" : "✏️ Update Payment"}
              </h2>
              {mode === "update" && (
                <span className="mode-tag" style={{ marginTop: 4, display: "inline-block" }}>UPDATE MODE</span>
              )}
            </div>
            <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={resetForm}>
              ✕ Cancel
            </button>
          </div>

          {mode === "create" && (
            <SearchableDropdown
              label="Select Order *"
              placeholder="Search by client, item, or order ID..."
              value={selectedOrder}
              onSelect={o => setFormField("orderId", o.orderId)}
              onClear={() => { setFormField("orderId", ""); setSelectedOrder(null); }}
              items={activeOrders}
              renderLabel={o => `${o.orderId} — ${o.client} · Balance: ${fmt(Math.max(o.balance, 0))}`}
              renderItem={o => (
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{o.client}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{o.item}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: "#E94560", fontFamily: "monospace" }}>{o.orderId}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#EF4444" }}>Balance: {fmt(Math.max(o.balance, 0))}</div>
                  </div>
                </div>
              )}
              emptyMsg="No unpaid orders found"
            />
          )}

          {selectedOrder && mode === "create" && (
            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              <strong>Outstanding balance:</strong> {fmt(Math.max(selectedOrder.balance, 0))}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Amount (₦) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={e => setFormField("amount", e.target.value)}
                placeholder={selectedOrder ? `Max: ${fmt(selectedOrder.balance)}` : "Enter amount"}
              />
            </div>
            <div className="form-group">
              <label>Payment Method</label>
              <select value={form.method} onChange={e => setFormField("method", e.target.value)}>
                <option value="Cash">💵 Cash</option>
                <option value="Transfer">🏦 Bank Transfer</option>
                <option value="POS">💳 POS / Card</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date</label>
              <input type="date" value={form.date} onChange={e => setFormField("date", e.target.value)} />
            </div>
            <div className="form-group">
              <label>Reference / Notes</label>
              <input
                value={form.notes}
                onChange={e => setFormField("notes", e.target.value)}
                placeholder="Transaction reference, cheque number, etc."
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={submit} disabled={submitting}>
              {mode === "create" ? "💰 Record Payment" : "💾 Update Payment"}
            </button>
            <button className="btn btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Daily Reconciliation Section */}
      <div className="card" style={{ borderLeft: "4px solid #3B82F6" }}>
        <div className="section-header" style={{ cursor: "pointer" }} onClick={() => setShowRecon(v => !v)}>
          <div>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>🧾 Daily / Period Reconciliation</h2>
            <p style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
              Match system records with your bank account and cash drawer
            </p>
          </div>
          <span style={{ fontSize: 12, color: "#3B82F6", fontWeight: 600 }}>{showRecon ? "▲ Hide" : "▼ Show"}</span>
        </div>

        {showRecon && (
          <div style={{ marginTop: 16 }}>
            <div className="form-row">
              <div className="form-group">
                <label>From Date *</label>
                <input type="date" value={reconFrom} onChange={e => setReconFrom(e.target.value)} />
              </div>
              <div className="form-group">
                <label>To Date *</label>
                <input type="date" value={reconTo} onChange={e => setReconTo(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" onClick={loadRecon} disabled={reconLoading}>
              {reconLoading ? "Loading..." : "🔍 Run Reconciliation"}
            </button>

            {recon && (
              <div style={{ marginTop: 20 }}>
                {/* Order Payments */}
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20 }}>💳</span> Order Payments Collected
                  </h3>
                  <div className="stats-grid-3">
                    {Object.entries(recon.orderPayments.byMethod).map(([m, v]) => (
                      <div key={m} className="stat-card">
                        <div className="lbl">{m}</div>
                        <div className="val success">{fmt(v)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="preview-box" style={{ marginTop: 8 }}>
                    <strong>Total:</strong> {fmt(recon.orderPayments.total)} ({recon.orderPayments.count} payments)
                  </div>
                </div>

                {/* Walk-in Sales */}
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20 }}>🛒</span> Walk-in Sales Collected
                  </h3>
                  <div className="stats-grid-3">
                    {Object.entries(recon.walkInSales.byMethod).map(([m, v]) => (
                      <div key={m} className="stat-card">
                        <div className="lbl">{m}</div>
                        <div className="val info">{fmt(v)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="preview-box" style={{ marginTop: 8 }}>
                    <strong>Total:</strong> {fmt(recon.walkInSales.total)} ({recon.walkInSales.count} sales)
                  </div>
                </div>

                {/* Combined Total */}
                <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 12, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#166534", marginBottom: 4 }}>GRAND TOTAL CASH IN</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: "#10B981" }}>{fmt(recon.combined.total)}</div>
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280" }}>
                      {new Date(recon.from).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })} — {new Date(recon.to).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  </div>
                  <div className="divider" style={{ margin: "12px 0" }} />
                  <div className="stats-grid-3">
                    {Object.entries(recon.combined.byMethod).map(([m, v]) => (
                      <div key={m}>
                        <span style={{ fontSize: 10, color: "var(--text-3)" }}>{m}</span>
                        <div style={{ fontWeight: 700, color: m === "Cash" ? "#10B981" : m === "Transfer" ? "#3B82F6" : "#F59E0B" }}>{fmt(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payments Log Section */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 16px 0 16px" }}>
          <div className="section-header">
            <div>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>💰 Payment History</h2>
              <p style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{total} payment record{total !== 1 ? "s" : ""} — refunded records are marked separately</p>
            </div>
            {isManager && (
              <button className="btn-sm btn-edit" onClick={api.exportPayments} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                ⬇ Export CSV
              </button>
            )}
          </div>

          {/* Search and Filters */}
          <div className="search-bar" style={{ marginTop: 12 }}>
            <input
              placeholder="🔍 Search by payment ID, order ID or client..."
              value={searchQ}
              onChange={e => { setSearchQ(e.target.value); setPage(1); }}
            />
            <select value={methodF} onChange={e => { setMethodF(e.target.value); setPage(1); }}>
              <option value="">All Methods</option>
              <option value="Cash">💵 Cash</option>
              <option value="Transfer">🏦 Transfer</option>
              <option value="POS">💳 POS</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <div className="form-group" style={{ flex: 1, minWidth: 130, margin: 0 }}>
              <label style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                style={{ padding: "7px 10px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, width: "100%" }}
              />
            </div>
            <div className="form-group" style={{ flex: 1, minWidth: 130, margin: 0 }}>
              <label style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); }}
                style={{ padding: "7px 10px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, width: "100%" }}
              />
            </div>
            {(dateFrom || dateTo || methodF || searchQ) && (
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button
                  className="btn-sm btn-secondary"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                    setMethodF("");
                    setSearchQ("");
                    setPage(1);
                  }}
                >
                  ✕ Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="spinner">Loading payments...</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Order ID</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Method</th>
                  <th>Date</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center", color: "var(--text-3)", padding: 48 }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
                      <div>No payments recorded yet</div>
                      {canLogPayment && (
                        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowForm(true)}>
                          + Record your first payment
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  payments.map(p => {
                    const refundState = paymentRefundState(p);
                    return (
                    <tr key={p.paymentId}>
                      <td style={{ fontFamily: "monospace", fontSize: 11, color: "var(--text-2)" }}>{p.paymentId}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>{p.orderId}</td>
                      <td style={{ fontWeight: 600 }}>{p.clientName || "—"}</td>
                      <td style={{ fontWeight: 700, color: refundState?.complete ? "var(--text-3)" : "#10B981", textDecoration: refundState?.complete ? "line-through" : "none" }}>{fmt(p.amount)}</td>
                      <td>
                        {refundState ? (
                          <span title={refundState.detail} style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: refundState.complete ? "#FEE2E2" : "#FEF3C7", color: refundState.complete ? "#B91C1C" : "#92400E" }}>
                            {refundState.label}
                          </span>
                        ) : (
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, background: "#DCFCE7", color: "#166534" }}>Collected</span>
                        )}
                        {refundState && <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 3, maxWidth: 145 }}>{refundState.detail}</div>}
                      </td>
                      <td>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          background: `${getMethodColor(p.method)}20`,
                          color: getMethodColor(p.method)
                        }}>
                          {p.method === "Cash" && "💵"}
                          {p.method === "Transfer" && "🏦"}
                          {p.method === "POS" && "💳"}
                          {" "}{p.method}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, color: "var(--text-2)" }}>{toDate(p.date)}</td>
                      <td style={{ fontSize: 11, color: "var(--text-3)", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.notes || "—"}
                      </td>
                     <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          {!refundState && <button
                            className="btn-sm btn-edit"
                            onClick={() => {
                              const ord = activeOrders.find(o => o.orderId === p.orderId) || {
                                orderId: p.orderId,
                                client: p.clientName || "",
                                item: "",
                                balance: 0
                              };
                              printReceipt(p, ord, user.businessName, user.businessLogo, user.planFeatures?.hasCustomBranding);
                            }}
                            title="Print Receipt"
                          >
                            🖨️
                          </button>}
                          {!refundState && user.planFeatures?.hasWhatsappTools !== false && (
                            <button className="btn-sm btn-edit" onClick={() => sharePaymentReceipt(p)} title="Share payment receipt via WhatsApp">Share WhatsApp</button>
                          )}
                          {isManager && !refundState && p.productionStatus !== "Delivered" && (
                            <button
                              className="btn-sm btn-edit"
                              onClick={() => {
                                setForm({
                                  paymentId: p.paymentId,
                                  orderId: p.orderId,
                                  amount: p.amount,
                                  date: toDate(p.date),
                                  method: p.method,
                                  notes: p.notes || ""
                                });
                                setMode("update");
                                setShowForm(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)" }}>
          <Pager page={page} total={total} per={20} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}

export default Payments;
